import { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import { Box, Button, Heading, Inline, Link } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { useIsExperimentActivated } from '@99designs/experiments';
import { IconEdit1 } from '@99designs/icons';
import { vistaI18nRoutesBy99dLocale } from '@99designs/routing';
import { useSite } from '@99designs/spa-common';
import { appendUtmParamsToUrl } from '@99designs/tracking';
import { PrintProductRecommendationsQuery } from '../../graph';
import { useCreateAssetForProjectMutation } from '../../graph/createAssetForProject/createAssetForProject.generated';
import events from '../../lib/events';
import PrintRecommendationsLogoSelectorModal from '../PrintRecommendationsLogoSelectorModal';
import PrintRecommendationItem from './PrintRecommendationItem';
import { RecommendationsCarouselContainer } from './RecommendationsCarouselContainer';

const MobileButtons = styled.div`
    margin-top: ${spaces['1bu']};

    svg {
        margin-left: ${spaces['0.5bu']};
    }

    ${mediaQuery.minimum('normal')} {
        display: none;
    }
`;

type PersonlizedBannerProps = {
    logoImages: ({
        id: string;
    } & Parameters<typeof PrintRecommendationsLogoSelectorModal>[0]['options'][0])[];
    data: PrintProductRecommendationsQuery | undefined;
    imageUrl: string;
    setImageUrl: React.Dispatch<React.SetStateAction<string>>;
};

function PrintRecommendationsBannerPersonalized({
    logoImages,
    data,
    imageUrl,
    setImageUrl,
}: PersonlizedBannerProps) {
    const site = useSite();
    const locale = site.siteConfig.locale;

    const [showLogoSelector, setShowLogoSelector] = useState(false);

    const recommendedItems = (data?.printProductRecommendations.recommendedProducts || [])
        .slice(0, 4)
        .map((item, index) => {
            if (index === 3) {
                return {
                    ...item,
                    title: __('More print options'),
                    vpProductPageUrl: appendUtmParamsToUrl(vistaI18nRoutesBy99dLocale(locale), {
                        utm_source: '99designs',
                        utm_medium: 'referral',
                        utm_campaign: '99d_yourwork_recs',
                    }),
                    altText: item.title,
                    estimatedPrice: undefined,
                };
            }

            return {
                ...item,
                title: item.title,
                altText: item.title,
                vpProductPageUrl: appendUtmParamsToUrl(item.vpProductPageUrl, {
                    utm_source: '99designs',
                    utm_medium: 'referral',
                    utm_campaign: '99d_yourwork_recs',
                }),
            };
        });

    useEffect(() => {
        events.trackRecommendationsViewed('your_work', 'personalized');
    }, []);

    const [createAssetForProject] = useCreateAssetForProjectMutation();
    const uploadToDamEnabled = useIsExperimentActivated('labs', 'enabled');

    const uploadFileToVista = () => {
        const fileId = logoImages.find((image) => image.previewUrl === imageUrl)?.id;
        if (fileId) {
            createAssetForProject({
                variables: {
                    input: {
                        fileId: fileId,
                    },
                },
            }).catch((error) => {
                Bugsnag.notify(error);
            });
        }
    };

    const handleSwapLogoClicked = () => {
        events.trackSwapLogoButton('your_work');
        setShowLogoSelector(true);
    };

    return (
        <>
            <Box paddingRight="3bu">
                <Heading accessibilityLevel={3}>
                    {__('You made a great logo. Now show it off!')}
                </Heading>
            </Box>
            <Box display="flex" flexWrap="wrap">
                <Box
                    display={['none', 'none', 'block']}
                    minWidth="190px"
                    flex="1 1 190px"
                    marginY="1bu"
                    marginRight="1.5bu"
                >
                    <Box>
                        {__(
                            'Swing by VistaPrint to see your logo on business cards, t-shirts and more.'
                        )}
                    </Box>
                    {logoImages.length > 1 && (
                        <Box marginTop="1bu">
                            <Button
                                icon={IconEdit1}
                                appearance="tertiary"
                                onClick={handleSwapLogoClicked}
                            >
                                {__('Swap logo file')}
                            </Button>
                        </Box>
                    )}
                </Box>
                <RecommendationsCarouselContainer>
                    {recommendedItems.map((item) => (
                        <PrintRecommendationItem
                            key={item.title}
                            title={item.title}
                            url={item.vpProductPageUrl}
                            image={item.previewImageUrl}
                            estimatedPrice={item.estimatedPrice}
                            onClick={() => {
                                uploadToDamEnabled && uploadFileToVista();
                                events.trackRecommendationsItemClicked(
                                    'your_work',
                                    item.vpProductPageUrl.split('?')[0],
                                    item.title,
                                    'personalized'
                                );
                            }}
                            altText={item.altText}
                        />
                    ))}
                </RecommendationsCarouselContainer>
                <MobileButtons>
                    <Inline space="1bu">
                        {logoImages.length > 1 && (
                            <Link onClick={handleSwapLogoClicked}>
                                {__('Swap logo')}
                                <IconEdit1 />
                            </Link>
                        )}
                    </Inline>
                </MobileButtons>
            </Box>
            {showLogoSelector && (
                <PrintRecommendationsLogoSelectorModal
                    options={logoImages}
                    onSelected={(imageUrl) => {
                        if (imageUrl) {
                            events.trackLogoFileSwapped('your_work');
                            setImageUrl(imageUrl);
                        }
                        setShowLogoSelector(false);
                    }}
                    initiallySelectedUrl={imageUrl}
                />
            )}
        </>
    );
}

export default PrintRecommendationsBannerPersonalized;
